import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  EnvelopeIcon, 
  MapPinIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { scrollToElement } from '../../utils/helpers';

const Footer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const quickLinks = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Vision', href: '#vision' },
  ];


  const handleNavClick = (href: string) => {
    if (href.startsWith('#')) {
      const elementId = href.substring(1);
      if (location.pathname === '/') {
        scrollToElement(elementId);
      } else {
        navigate('/' + href);
      }
    }
  };

  return (
    <footer id="contact" className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-4 mb-6">
                <img 
                  src="/logo192.png" 
                  alt="Kreed Odisha Logo" 
                  className="w-16 h-16 object-contain"
                />
                <div className="flex flex-col border-l border-gray-700 pl-4">
                  <span className="font-bold text-2xl text-white tracking-tight">Kreed Odisha</span>
                  <span className="text-primary-400 font-medium">The Pathway to Sporting Excellence</span>
                </div>
              </div>
              
              <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
                Empowering Odisha's youth through comprehensive sports development. 
                From grassroots to glory, we're building champions who will make 
                our state and nation proud on the global stage.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-300">
                  <MapPinIcon className="w-5 h-5 text-primary-500 flex-shrink-0" />
                  <span>Bhubaneswar, Odisha, India</span>
                </div>
                
                <div className="flex items-center space-x-3 text-gray-300">
                  <EnvelopeIcon className="w-5 h-5 text-primary-500 flex-shrink-0" />
                  <span>kreedodisha@gmail.com</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold text-white mb-6 border-b border-primary-500/30 pb-2 inline-block">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => handleNavClick(link.href)}
                      className="text-gray-400 hover:text-primary-400 transition-all duration-200 text-left flex items-center group"
                    >
                      <span className="w-0 group-hover:w-2 h-0.5 bg-primary-500 mr-0 group-hover:mr-2 transition-all"></span>
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-lg font-bold text-white mb-6 border-b border-primary-500/30 pb-2 inline-block">Resources</h4>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <Link to="/apply" className="hover:text-primary-400 transition-colors flex items-center group">
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-500 mr-0 group-hover:mr-2 transition-all"></span>
                    Candidate Registration
                  </Link>
                </li>
                <li>
                  <Link to="/check-status" className="hover:text-primary-400 transition-colors flex items-center group">
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-500 mr-0 group-hover:mr-2 transition-all"></span>
                    Check Application Status
                  </Link>
                </li>
                <li>
                  <Link to="/admin/login" className="hover:text-primary-400 transition-colors flex items-center group">
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-500 mr-0 group-hover:mr-2 transition-all"></span>
                    Administrator Portal
                  </Link>
                </li>
                <li>
                  <Link to="/instructor/login" className="hover:text-primary-400 transition-colors flex items-center group">
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-500 mr-0 group-hover:mr-2 transition-all"></span>
                    Instructor Portal
                  </Link>
                </li>
              </ul>
            </div>

          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-gray-800 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h4 className="text-xl font-bold text-white mb-2">Stay Updated</h4>
              <p className="text-gray-300">
                Get the latest news about Kreed Odisha programs and success stories.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
              <button className="bg-gradient-to-r from-primary-600 to-success-600 hover:from-primary-700 hover:to-success-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-800 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Contact Email */}
            <div className="flex items-center space-x-3">
              <EnvelopeIcon className="w-5 h-5 text-primary-500" />
              <span className="text-gray-300">kreedodisha@gmail.com</span>
            </div>

            {/* Copyright */}
            <div className="flex items-center space-x-2 text-gray-400 text-sm">
              <span>© 2024 Kreed Odisha. Made with</span>
              <HeartIcon className="w-4 h-4 text-red-500" />
              <span>by <a href="https://hardinidynamics.com" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 transition-colors">Hardini Dynamics</a></span>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;